import type { SellerGamificationAchievement } from '../../../types/sellerGamification.types';

import { markCelebrationImmediate } from '../../shared/celebration/celebration.session';

const STORAGE_KEY = 'qumashi:celebrationQueue';

/** In-memory fallback when sessionStorage is unavailable (tests, SSR). */
let memoryQueue: SellerGamificationAchievement[] = [];

function readQueue(): SellerGamificationAchievement[] {
  try {
    if (typeof sessionStorage === 'undefined') return memoryQueue;
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as SellerGamificationAchievement[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return memoryQueue;
  }
}

function writeQueue(items: SellerGamificationAchievement[]) {
  memoryQueue = items;
  try {
    if (typeof sessionStorage === 'undefined') return;
    if (items.length === 0) {
      sessionStorage.removeItem(STORAGE_KEY);
    } else {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    }
  } catch {
    // sessionStorage unavailable — memoryQueue still updated
  }
}

function dedupeKey(item: SellerGamificationAchievement): string {
  return item.id ?? item.code;
}

/** Persist celebrations across route/provider boundaries (e.g. listing wizard → designs). */
export function enqueueCelebrations(
  items: SellerGamificationAchievement[],
  options?: { immediate?: boolean },
) {
  if (!items.length) return;
  if (options?.immediate) {
    markCelebrationImmediate();
  }
  const existing = readQueue();
  const merged = [...existing];
  for (const item of items) {
    const key = dedupeKey(item);
    if (!merged.some((m) => dedupeKey(m) === key)) {
      merged.push(item);
    }
  }
  writeQueue(merged);
}

export function peekCelebrationQueue(): SellerGamificationAchievement[] {
  return readQueue();
}

export function removeCelebrationFromQueue(unlockId: string) {
  writeQueue(readQueue().filter((c) => c.id !== unlockId && c.code !== unlockId));
}

export function clearCelebrationQueue() {
  writeQueue([]);
}

export function mergeCelebrations(
  serverPending: SellerGamificationAchievement[],
  queued: SellerGamificationAchievement[],
): SellerGamificationAchievement[] {
  const merged = [...serverPending];
  for (const item of queued) {
    const key = dedupeKey(item);
    if (!merged.some((m) => dedupeKey(m) === key)) {
      merged.push(item);
    }
  }
  return merged;
}

export function diffNewCelebrations(
  before: SellerGamificationAchievement[],
  after: SellerGamificationAchievement[],
): SellerGamificationAchievement[] {
  return after.filter(
    (c) => !before.some((p) => p.code === c.code || (p.id && p.id === c.id)),
  );
}
